import React, { useMemo, useState } from 'react';
import { Phone, ShieldCheck, X } from 'lucide-react';
import { useStore } from '../context/StoreContext';
import { UserRole } from '../types';
import { Button } from './Button';

const PHONE_PROMPT_DISMISSED_KEY = 'ft_phone_prompt_dismissed_session';

export const ContactPhonePrompt = () => {
  const { user, currentCompany, updateContactInfo } = useStore();
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(PHONE_PROMPT_DISMISSED_KEY) === '1');

  const shouldAsk = useMemo(() => {
    if (!user || user.role === UserRole.WAITER || dismissed) return false;
    if (user.role === UserRole.SAAS_ADMIN) return !user.phone;
    if (!currentCompany) return false;
    return !user.phone || !currentCompany.contactPhone;
  }, [currentCompany, dismissed, user]);

  if (!shouldAsk) return null;

  const title = user?.role === UserRole.SAAS_ADMIN
    ? 'Complete seu contato administrativo'
    : 'Confirme o telefone do estabelecimento';

  const description = user?.role === UserRole.SAAS_ADMIN
    ? 'Esse número ajuda a organizar a equipe FlowTab, responsáveis e avisos importantes.'
    : 'Esse número será usado como contato seguro da conta e suporte do FlowTab.';

  const handleDismiss = () => {
    sessionStorage.setItem(PHONE_PROMPT_DISMISSED_KEY, '1');
    setDismissed(true);
  };

  const handleSave = async () => {
    const cleanPhone = phone.replace(/[^\d+()\-\s]/g, '').trim();
    if (cleanPhone.replace(/\D/g, '').length < 10) {
      alert('Informe um telefone válido com DDD.');
      return;
    }
    setSaving(true);
    try {
      const success = await updateContactInfo(cleanPhone);
      if (!success) {
        alert('Não foi possível salvar o telefone agora.');
        return;
      }
      sessionStorage.setItem(PHONE_PROMPT_DISMISSED_KEY, '1');
      setDismissed(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center bg-slate-950/45 p-3 backdrop-blur-sm sm:items-center">
      <div className="w-full max-w-md overflow-hidden rounded-[2rem] border border-white/70 bg-white shadow-2xl shadow-slate-950/20">
        <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-900 p-5 text-white">
          <div className="flex items-start justify-between gap-4">
            <div className="rounded-2xl bg-emerald-300/15 p-3 text-emerald-200">
              <Phone className="h-6 w-6" />
            </div>
            <button onClick={handleDismiss} className="rounded-full bg-white/10 p-2 text-white/70 transition hover:bg-white/20 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          </div>
          <h2 className="mt-4 text-2xl font-black tracking-tight">{title}</h2>
          <p className="mt-2 text-sm font-semibold leading-6 text-slate-300">{description}</p>
        </div>

        <div className="p-5">
          <label className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">Telefone / WhatsApp</label>
          <input
            autoFocus
            value={phone}
            onChange={event => setPhone(event.target.value)}
            placeholder="Ex: (31) 99999-9999"
            className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 p-4 text-lg font-black text-slate-950 outline-none transition focus:border-emerald-400"
          />
          <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 p-3 text-xs font-bold leading-5 text-emerald-800">
            <ShieldCheck className="mr-1 inline h-4 w-4" />
            Cliente não vê a área administrativa. Esse aviso só pede o contato da própria conta.
          </div>
          <div className="mt-5 flex flex-col gap-2 sm:flex-row">
            <Button variant="secondary" onClick={handleDismiss} className="rounded-2xl sm:flex-1">
              Lembrar depois
            </Button>
            <Button onClick={handleSave} disabled={saving} className="rounded-2xl sm:flex-1">
              Salvar telefone
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
